'use client';

import { useEffect, useRef } from 'react';

import type { PostCommentSearchItem } from '@ps/api/types';

import { CommentRow } from './CommentRow';

interface CommentListProps {
  comments: PostCommentSearchItem[];
  isInitializing: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}

/**
 * Root-comment list with loading / empty / populated states. When more pages
 * exist it renders a sentinel at the bottom and auto-loads the next page as it
 * scrolls into view (infinite scroll) — `onLoadMore` is idempotent, so the
 * re-observe-on-append loop that keeps loading while the sentinel stays visible
 * can't double-fetch. No own scroll or wrapper chrome so the host composes it.
 */
export function CommentList({
  comments,
  isInitializing,
  hasMore,
  onLoadMore,
}: CommentListProps) {
  const sentinelRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (!hasMore) {
      return;
    }
    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return;
    }
    // Pre-load a little before the sentinel reaches the viewport. Works whether
    // the scroll container is the page (mobile) or the comments column (desktop) —
    // IntersectionObserver clips against intermediate scroll ancestors. Re-observed
    // on each append (`comments.length` dep) so it keeps loading while in view.
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) {
          onLoadMore();
        }
      },
      { rootMargin: '300px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore, comments.length]);

  if (isInitializing && comments.length === 0) {
    return (
      <div className="py-8 text-center text-[13px] text-black/50">
        Loading comments…
      </div>
    );
  }

  if (comments.length === 0) {
    return (
      <div className="py-8 text-center text-[13px] text-black/50">
        No comments yet. Be the first to comment!
      </div>
    );
  }

  return (
    <ul className="flex flex-col gap-4">
      {comments.map(comment => (
        <CommentRow key={comment.id} comment={comment} />
      ))}
      {hasMore ? (
        <li ref={sentinelRef} className="flex justify-center py-3">
          <span
            className="h-5 w-5 animate-spin rounded-full border-2 border-black/15 border-t-black/50"
            aria-label="Loading more comments"
          />
        </li>
      ) : null}
    </ul>
  );
}
